import { Box, Table } from '@mui/material';

import { TableOverlaySpinner } from '~/components/Spinner/TableOverlaySpinner';
import { generateTableRowsSkeleton, TableBody } from '~/components/Table';
import { EmptyTableContent } from '~/components/Table/EmptyTableContent';
import { useCachedData } from '~/hooks/useCachedData';
import { useTableRowsSelection } from '~/hooks/useTableRowsSelection';
import { Invoice } from '~/types/invoice';
import { SortDirection } from '~/types/table';

import { headCells } from './config';
import { TableHead } from './TableHead';
import { TableRow } from './TableRow';

export type Props = {
  items?: Invoice[];
  count?: number;
  isLoading?: boolean;
  currentSort?: keyof Invoice | string;
  sortDirection?: SortDirection;
  onSort: (column: keyof Invoice | string) => void;
};

export const InvoicesTable = ({
  items,
  count,
  isLoading = false,
  currentSort,
  sortDirection,
  onSort,
}: Props) => {
  const cached = useCachedData<Invoice[]>(items);

  const { selected, selectedAll, onSelect, onSelectAll, onClearSelection } = useTableRowsSelection(
    count,
    items?.map((item) => item.id),
  );

  return (
    <Table>
      <TableHead
        headCells={headCells}
        currentSort={currentSort}
        sortDirection={sortDirection}
        onSort={onSort}
        selected={selected}
        selectedAll={selectedAll}
        onSelectAll={onSelectAll}
        onClearSelection={onClearSelection}
      />
      <TableBody isEmpty={!!cached?.length}>
        {generateTableRowsSkeleton(headCells, isLoading && !cached, true)}

        {cached?.map((row) => (
          <TableRow
            key={row.id}
            row={row}
            selected={selected.includes(row.id)}
            onSelect={onSelect}
          />
        ))}

        {!cached?.length && !isLoading && (
          <EmptyTableContent headCells={headCells} hasCheckbox>
            <Box
              component="img"
              sx={{
                height: 300,
                width: 300,
                maxHeight: { xs: 160, md: 300 },
                maxWidth: { xs: 160, md: 300 },
                resize: 'cover',
              }}
              alt="The house from the offer."
              src={require('../../../../assets/images/Invoice.png')}
            />
          </EmptyTableContent>
        )}

        {!!cached?.length && isLoading && <TableOverlaySpinner />}
      </TableBody>
    </Table>
  );
};
